import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './QuickAccessForm.css';

export default function QuickAccessForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [sectionId, setSectionId] = useState(42);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});

  // Form values (auto-filled from content_published)
  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    cards: [
      { id: 'apply', title: '', text: '' },
      { id: 'calendar', title: '', text: '' },
      { id: 'contact', title: '', text: '' }
    ]
  });

  // Pending approvals per field
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    'cards.0': false,
    'cards.1': false,
    'cards.2': false
  });

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({...formData});

  useEffect(() => {
    loadQuickAccessSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadQuickAccessSection = async () => {
    setLoading(true);
    try {
      // Try the approval workflow endpoint first
      let response = await fetch(
        `${apiBase}/cms/section/42/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      // If that fails with 401, try the content endpoint
      if (response.status === 401) {
        response = await fetch(
          `${apiBase}/content?menu=homepage`,
          { 
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}`);
      }

      const result = await response.json();

      // Handle both response formats
      let section = null;
      let pending_changes = [];

      if (result.success && result.data) {
        // From cms/section endpoint
        const data = result.data;
        if (data.section) {
          section = data.section;
          pending_changes = data.pending_changes || [];
        }
      } else if (result.ok && Array.isArray(result.data)) {
        // From /content endpoint - find quicklinks section
        section = result.data.find(s => s.section === 'quick_links');
      }

      if (section && section.content_published) {
        let published = section.content_published;
        
        if (typeof published === 'string') {
          try {
            published = JSON.parse(published);
          } catch (e) {
            published = {};
          }
        }

        const publishedEn = published.en || {};

        // Auto-fill form with published data
        const initialData = {
          kicker: publishedEn.kicker || '',
          title: publishedEn.title || '',
          cards: [
            { id: 'apply', title: publishedEn.cards?.[0]?.title || '', text: publishedEn.cards?.[0]?.text || '' },
            { id: 'calendar', title: publishedEn.cards?.[1]?.title || '', text: publishedEn.cards?.[1]?.text || '' },
            { id: 'contact', title: publishedEn.cards?.[2]?.title || '', text: publishedEn.cards?.[2]?.text || '' }
          ]
        };

        setFormData(initialData);
        setOriginalData(initialData);
        setSectionId(section.id || 42);

        // Mark pending fields
        if (Array.isArray(pending_changes)) {
          const pendingMap = {
            kicker: false,
            title: false,
            'cards.0': false,
            'cards.1': false,
            'cards.2': false
          };

          pending_changes.forEach(change => {
            if (change.status === 'pending' && change.locale === 'en') {
              pendingMap[change.field_id] = true;
            }
          });

          setPendingFields(pendingMap);
        }
      } else {
        toast.warning('Quick Access section not found in database');
        setFormData({
          kicker: '',
          title: '',
          cards: [
            { id: 'apply', title: '', text: '' },
            { id: 'calendar', title: '', text: '' },
            { id: 'contact', title: '', text: '' }
          ]
        });
      }
    } catch (err) {
      console.error('Error loading quick access section:', err);
      toast.error('Error loading quick access section: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleCardChange = (cardIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      cards: prev.cards.map((card, idx) =>
        idx === cardIndex ? { ...card, [field]: value } : card
      )
    }));
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Check if value changed
    const hasChanged = fieldName.startsWith('cards.')
      ? JSON.stringify(formData[fieldName.split('.')[0]][parseInt(fieldName.split('.')[1])]) !== 
        JSON.stringify(originalData[fieldName.split('.')[0]][parseInt(fieldName.split('.')[1])])
      : formData[fieldName] !== originalData[fieldName];

    if (!hasChanged) {
      toast.warning('No changes to submit');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const newValue = fieldName.startsWith('cards.')
        ? formData.cards[parseInt(fieldName.split('.')[1])]
        : formData[fieldName];

      const oldValue = fieldName.startsWith('cards.')
        ? originalData.cards[parseInt(fieldName.split('.')[1])]
        : originalData[fieldName];

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: fieldName.replace(/_/g, ' ').replace(/\./g, ' '),
            old_value: oldValue,
            new_value: newValue,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        // Mark as pending locally
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        // Update original data
        if (fieldName.startsWith('cards.')) {
          const cardIdx = parseInt(fieldName.split('.')[1]);
          setOriginalData(prev => ({
            ...prev,
            cards: prev.cards.map((card, idx) =>
              idx === cardIdx ? { ...formData.cards[cardIdx] } : card
            )
          }));
        } else {
          setOriginalData(prev => ({
            ...prev,
            [fieldName]: formData[fieldName]
          }));
        }
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const getCardLabel = (id) => {
    const labels = {
      'apply': 'Apply Now',
      'calendar': 'School Calendar',
      'contact': 'Contact Us'
    };
    return labels[id] || id;
  };

  if (loading) {
    return (
      <div className="quick-access-form-container">
        <div className="loading">Loading quick access section...</div>
      </div>
    );
  }

  return (
    <div className="quick-access-form-container">
      <ToastContainer />

      <div className="quick-access-form">
        <h2>Quick Access Section</h2>
        <p className="form-description">Edit and submit for approval</p>

        {/* Kicker */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Kicker / Subtitle</label>
            {pendingFields.kicker && (
              <span className="status-badge pending">⏳ Pending</span>
            )}
          </div>
          <input
            type="text"
            maxLength="100"
            value={formData.kicker}
            onChange={(e) => handleFieldChange('kicker', e.target.value)}
            disabled={submitting.kicker || pendingFields.kicker}
            placeholder="e.g., Quick Access"
            className="text-input"
          />
          <button
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
            className="submit-btn"
          >
            {submitting.kicker ? 'Submitting...' : 'Submit'}
          </button>
        </div>

        {/* Title */}
        <div className="form-field-card">
          <div className="field-header">
            <label>Section Title</label>
            {pendingFields.title && (
              <span className="status-badge pending">⏳ Pending</span>
            )}
          </div>
          <input
            type="text"
            maxLength="150"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g., Everything you need at a glance"
            className="text-input"
          />
          <button
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
            className="submit-btn"
          >
            {submitting.title ? 'Submitting...' : 'Submit'}
          </button>
        </div>

        {/* Cards */}
        <div className="cards-section">
          <h3 className="cards-title">Quick Access Cards</h3>
          <p className="cards-description">Edit individual cards</p>

          {formData.cards.map((card, idx) => (
            <div key={card.id} className="form-field-card card-item">
              <div className="field-header">
                <label>{getCardLabel(card.id)}</label>
                {pendingFields[`cards.${idx}`] && (
                  <span className="status-badge pending">⏳ Pending</span>
                )}
              </div>

              {/* Card Title */}
              <div className="card-subfield">
                <label className="subfield-label">Card Title</label>
                <input
                  type="text"
                  maxLength="50"
                  value={card.title}
                  onChange={(e) => handleCardChange(idx, 'title', e.target.value)}
                  disabled={submitting[`cards.${idx}`] || pendingFields[`cards.${idx}`]}
                  placeholder="Card title"
                  className="text-input"
                />
              </div>

              {/* Card Text */}
              <div className="card-subfield">
                <label className="subfield-label">Card Description</label>
                <textarea
                  maxLength="200"
                  value={card.text}
                  onChange={(e) => handleCardChange(idx, 'text', e.target.value)}
                  disabled={submitting[`cards.${idx}`] || pendingFields[`cards.${idx}`]}
                  placeholder="Card description"
                  className="textarea-input"
                  rows="3"
                />
              </div>

              <button
                onClick={() => handleSubmitField(`cards.${idx}`)}
                disabled={
                  submitting[`cards.${idx}`] ||
                  pendingFields[`cards.${idx}`] ||
                  (formData.cards[idx].title === originalData.cards[idx].title &&
                   formData.cards[idx].text === originalData.cards[idx].text)
                }
                className="submit-btn"
              >
                {submitting[`cards.${idx}`] ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
